import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Formik } from 'formik';
import * as Yup from 'yup';
import { Typography, Collapse, Alert, AlertTitle } from '@mui/material';
import AuthInput from '../../components/authInput';
import AuthButton from '../../components/authSubmitButton';
import FormikErrorFocus from 'formik-error-focus';

import { IState } from '../../../../shared/utilities/interfaces';
import { createAccountAction } from '../../store/actions';
import {   AccountType, BrandSignUpFormBody } from 'features/auth/utilites';
import { AccountTypes } from '../../../../core/constants';
import { closeModal } from '../../../../core/store/app/actions';
import { ExternalLinkInline } from '../creatorSignUp/index.styles';
import { getBrandList } from '../../store/api';
import AutocompleteComponent from 'shared/components/autoComplete';

interface IBrandSignUp {
  // eslint-disable-next-line no-unused-vars
  createAccount: (data: BrandSignUpFormBody, type: AccountType) => void;
  createAccountStatus: any;
  userPayload:BrandSignUpFormBody,
  closeAlert: () => void;
};

const brandSignUpValidation = () => {
  const updatePasswordSchema = {
    email: Yup
      .string()
      .strict(true)
      .trim('First name should not include trailing white spaces')
      .email("You have entered incorrect email address")
      .required('This field is required'),
    firstName: Yup
      .string()
      .strict(true)
      .trim('First name should not include trailing white spaces')
      .max(40, 'First name should be 40 chars atmost')
      .min(1, 'First name should be 1 chars atleast')
      .required('This field is required'),
    lastName: Yup
      .string()
      .strict(true)
      .trim('Last name should not include trailing white spaces')
      .max(40, 'Last name should be 40 chars atmost')
      .min(1, 'Last name should be 1 chars atleast')
      .required('This field is required'),
    jobTitle: Yup
      .string()
      .strict(true)
      .trim('Job title should not include trailing white spaces')
      .max(40, 'Job title should be 40 chars atmost')
      .min(2, 'Job title should be 2 chars atleast')
      .required('This field is required'),
    brandId: Yup
      .string()
      .strict(true)
      .trim('Brand name should not include trailing white spaces')
      .max(40, 'Brand name should be 40 chars atmost')
      .min(2, 'Brand name should be 2 chars atleast')
      .required('This field is required'),
    brandName: Yup
      .string()
      .when("brandId", {
        is: "Other",
        then: Yup.string()
          .strict(true)
          .required("This field is required")
          .trim('Brand name should not include trailing white spaces')
          .max(40, 'Brand name should be 40 chars atmost')
          .min(2, 'Brand name should be 2 chars atleast'),
        otherwise: Yup.string()
      }),
    password: Yup
      .string()
      .strict()
      .trim('Password should not include trailing whitespaces')
      .required('This field is required')
      .matches(
        new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z]).{8,}$"),
        'Password should be at least 8 chars long, must contains at least 1 uppercase letter, 1 lowercase letter and 1 number or 1 symbol'
      )
      .min(
        8,
        'Password should be minimum 8 chars atleast'
      ),
  };
  return Yup.object().shape(updatePasswordSchema);
};

function BrandSignUp(brandSignUpProps: IBrandSignUp) {
  const {
    closeAlert,
    userPayload,
    createAccount
  } = brandSignUpProps;
  const [brandList, setBrandList] = useState([]);

  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    brandId: '',
    brandName: '',
    password: '',
  };

  const submitForm = async (data: typeof initialValues) => {
    closeAlert();
    if (data.brandId !== 'Other') {
      const brand = brandList.find(item => item.value === data.brandId);
      data.brandName = brand.fieldLabel;
    }
    createAccount(data, AccountTypes.BRAND);

  };

  useEffect(() => {
    getBrandList()
      .then(res => {
        const { data, code } = res.data;
        if (code === 200) {
          const brandListWithLabel = data.map(item => ({
            value: item._id,
            fieldLabel: item.brandName
          }));
          setBrandList([{
            value: 'Other',
            fieldLabel: 'Add a brand'
          },...brandListWithLabel]);
        }
      })
      .catch(e => {
        return(
          <Alert severity='error' >
            <AlertTitle>
              {e.status}
            </AlertTitle>
            {e.message}
          </Alert>
        );
      });
  }, []);



  return (<Formik
    initialValues={userPayload?userPayload:initialValues}
    onSubmit={submitForm}
    validationSchema={brandSignUpValidation()}
    enableReinitialize
    validateOnBlur
  >
    {({ handleSubmit, values }) => {
      return (
        <form onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          handleSubmit();
        }}>
          <AuthInput
            variant='standard'
            label='Work Email'
            name='email'
            autoFocus
          />
          <AuthInput
            variant='standard'
            label='First Name'
            name='firstName'
          />
          <AuthInput
            variant='standard'
            label='Last Name'
            name='lastName'
          />
          <AuthInput
            variant='standard'
            label='Job Title'
            name='jobTitle'
          />
          <AutocompleteComponent
            options={brandList}
            id='brandId'
            name='brandId'
            label='Brand Name'
            variant='standard'
          />
          <Collapse
            in={values.brandId === 'Other'}
          >
            <AuthInput
              variant='standard'
              label='Enter The Brand Name'
              name='brandName'
            />
          </Collapse>
          <AuthInput
            
            variant='standard'
            label='Password'
            name='password'
            type='password'
          />
          <Typography
            variant='subtitle2'
            sx={{ mt: 1, mb: 1, color: 'claraPalette.gray1' }}
          >
            By clicking Create account, I agree that I have read
            and accepted the&nbsp;
            <ExternalLinkInline
              href='/terms-and-conditions'
              target='_blank'
            >
              Terms of Use
            </ExternalLinkInline>
            &nbsp;and&nbsp;
            <ExternalLinkInline
              href='/privacy-policy'
              target='_blank'
            >
              Privacy Policy
            </ExternalLinkInline>
          </Typography>
          <AuthButton variant="contained" type="submit">
            Create Account
          </AuthButton>
          <FormikErrorFocus
            offset={0}
            align="middle"
            focusDelay={10}
            ease="linear"
            duration={500}
          />
        </form>
      );
    }}
  </Formik>);
}

export default connect((state: IState) => ({
  createAccountStatus: state.auth.status,
  userPayload: state.auth.userPayload,
}), {
  createAccount: createAccountAction,
  closeAlert: closeModal
})(BrandSignUp);